import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { emptyDraft, type RegistrationDraft } from './types';

export interface WizardState {
  stepIndex: number;
  draft: RegistrationDraft;
  setStep: (index: number) => void;
  patch: <K extends keyof RegistrationDraft>(key: K, value: RegistrationDraft[K]) => void;
  reset: () => void;
}

export const useWizard = create<WizardState>()(
  persist(
    (set) => ({
      stepIndex: 0,
      draft: emptyDraft(),
      setStep: (stepIndex) => set({ stepIndex }),
      patch: (key, value) =>
        set((state) => ({ draft: { ...state.draft, [key]: value } })),
      reset: () => {
        set({ stepIndex: 0, draft: emptyDraft() });
        // Belt-and-braces: drop the persisted key so nothing rehydrates if a
        // future load races the in-memory set.
        try {
          void useWizard.persist.clearStorage();
        } catch {
          /* ignore */
        }
      },
    }),
    { name: 'pgs:wizard:v3' },
  ),
);

// Ordered visible steps — CIS Assessment and UTR are conditionally included
// after Payment (P60/P45 employment situation) when the applicant works in construction.
export const visibleStepIds = (cis: boolean): string[] => {
  const base = ['personal', 'referral', 'agency', 'bank', 'occupation', 'payment'];
  if (cis) base.push('cis-assessment', 'utr');
  base.push('documents', 'review');
  return base;
};
